import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { MessageCircle, CalendarDays, Truck, ShieldCheck, Store, Share2 } from "lucide-react";
import { getProduct, getRelated, fmtPrice, waLink, waShareLink, skuFromHandle, productUrl, addRecentlyViewed, fetchGoldRates } from "../lib/store";
import ProductCard, { InquiryDialog } from "../components/ProductCard";
import { WishlistButton } from "../components/WishlistButton";
import { ZoomImage } from "../components/ZoomImage";
import { PhotoViewer } from "../components/PhotoViewer";
import { useEdit } from "../lib/content";
import { ProductEditModal, autoPrice } from "../components/edit/ProductEditModal";
import { Pencil } from "lucide-react";
import { ProductStory, storyKeyFor } from "../components/ProductStory";
import { expOn } from "../lib/experience";
import RecentlyViewed from "../components/RecentlyViewed";
import Seo, { breadcrumbSchema, productSchema } from "../components/Seo";
import { CreditNotice } from "../components/CreditNotice";
import { trackViewItem, trackContactClick } from "../lib/analytics";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";

export default function ProductDetail() {
  const { handle } = useParams();
  const product = getProduct(handle);
  const [imgIdx, setImgIdx] = useState(0);
  const [inquiryOpen, setInquiryOpen] = useState(false);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [rev, setRev] = useState(0);
  const [gold, setGold] = useState(null);
  const { isOwner, editMode } = useEdit();

  useEffect(() => { fetchGoldRates().then(setGold).catch(() => {}); }, []);

  useEffect(() => { 
    setImgIdx(0); 
    window.scrollTo(0, 0); 
    if (product) {
      addRecentlyViewed(product.handle);
      trackViewItem(product);
      localStorage.setItem("rj_last_category", storyKeyFor(product));
    }
  }, [handle, product]);

  if (!product) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-32 text-center">
        <h1 className="font-heading text-3xl text-[#171310]">Piece Not Found</h1>
        <Link to="/shop"><Button className="mt-6 rounded-none bg-[#171310] text-[#e9d9ac] hover:bg-[#3a2f1d]">Browse Collection</Button></Link>
      </div>
    );
  }

  const related = getRelated(product, 4);
  const livePrice = autoPrice(product, gold);
  const shownPrice = livePrice != null ? livePrice : product.price;
  const onSale = product.compareAtPrice && product.compareAtPrice > shownPrice;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-10" data-rev={rev}>
      <Seo
        title={product.seoTitle || (product.title.length > 45 ? product.title.slice(0, 45).trim() : product.title)}
        description={product.seoDescription || `${product.title} — ${fmtPrice(shownPrice)} at Regal Jewels, Chicago. ${product.description ? product.description.slice(0, 90) : "Inquire online, on WhatsApp, or book an in-store viewing."}`}
        keywords={product.keywords}
        path={`/product/${product.handle}`}
        image={product.images[0]}
        type="product"
        jsonLd={[
          productSchema(product, shownPrice),
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Shop", path: "/shop" },
            { name: product.title, path: `/product/${product.handle}` },
          ]),
        ]}
      />
      <p className="text-xs tracking-[0.18em] uppercase text-[#8f8571] mb-8">
        <Link to="/" className="hover:text-[#a8842c] transition-colors">Home</Link> /{" "}
        <Link to="/shop" className="hover:text-[#a8842c] transition-colors">Shop</Link> /{" "}
        <span className="text-[#3a352c]">{product.title.slice(0, 50)}</span>
      </p>

      <div className="grid lg:grid-cols-2 gap-12">
        {/* Gallery */}
        <div>
          <ZoomImage src={product.images[imgIdx]} alt={product.title} onOpen={() => setViewerOpen(true)} />
          {product.images.length > 1 && (
            <div className="flex gap-3 mt-4">
              {product.images.map((img, i) => (
                <button key={img} onClick={() => { setImgIdx(i); setViewerOpen(true); }} data-testid={`product-thumb-${i}`} className={`w-20 h-20 bg-white border overflow-hidden transition-colors duration-200 ${i === imgIdx ? "border-[#a8842c]" : "border-[#eee6d6] hover:border-[#cdb87a]"}`}>
                  <img src={img} alt="" className="w-full h-full object-contain p-1.5" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Info */}
        <div>
          <div className="flex flex-wrap gap-2 mb-4">
            {product.tags.slice(0, 3).map((t) => (
              <Badge key={t} variant="outline" className="rounded-none border-[#d8cdb5] text-[#7a7160] font-normal tracking-wide">{t}</Badge>
            ))}
          </div>
          <h1 className="font-heading text-2xl sm:text-3xl text-[#171310] leading-snug">{product.title}</h1>
          {isOwner && editMode && (
            <button onClick={() => setEditOpen(true)} data-testid="product-edit-btn"
              className="mt-3 inline-flex items-center gap-2 rounded-none bg-[#171310] text-[#e9d9ac] text-[11px] tracking-[0.14em] uppercase px-4 py-2 hover:bg-[#3a2f1d]">
              <Pencil size={13} /> Edit this product
            </button>
          )}
          {expOn("voiceNarration") && <ProductStory product={product} />}

          <div className="flex items-baseline gap-3 mt-5">
            <span className="font-heading text-3xl text-[#171310]">{fmtPrice(shownPrice)}</span>
            {onSale && (
              <>
                <span className="text-lg text-[#9c9482] line-through">{fmtPrice(product.compareAtPrice)}</span>
                <Badge className="rounded-none bg-[#8c2f1b] text-white hover:bg-[#8c2f1b]">Sale</Badge>
              </>
            )}
          </div>
          {livePrice != null && (
            <p className="text-xs text-[#8a6a1f] mt-1" data-testid="live-price-note">
              At today's {product.karat || "22"}K gold rate · {product.gramWeight}g — updates automatically
            </p>
          )}
          {!(shownPrice > 0) && (
            <a
              onClick={() => trackContactClick("whatsapp_ask_price")}
              href={waLink(`Hello Regal Jewels! Could you share the price of: ${product.title}\nSKU: ${skuFromHandle(product.handle)}\n${productUrl(product.handle)}`)}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block mt-3"
              data-testid="pdp-ask-price-btn"
            >
              <Button className="rounded-none bg-[#c9a24b] hover:bg-[#dcb75f] text-[#0a0a0a] font-semibold tracking-[0.15em] uppercase text-xs h-10 px-5 transition-colors duration-300">
                <MessageCircle size={14} className="mr-2" /> Ask Price on WhatsApp
              </Button>
            </a>
          )}
          {product.variantInfo && (
            <p className="text-sm text-[#7a7160] mt-2">Specification: {product.variantInfo}</p>
          )}
          <p className={`text-sm mt-2 ${product.available ? "text-[#3f6b3a]" : "text-[#8c2f1b]"}`}>
            {product.available ? "● In stock — available for viewing & purchase" : "● Currently sold out — inquire for availability"}
          </p>
          <CreditNotice placement="product" className="mt-4" />

          {product.description && (
            <p className="text-[#5c5240] leading-relaxed mt-6 border-t border-[#e8e0d0] pt-6">{product.description}</p>
          )}

          {/* CTAs */}
          <div className="space-y-3 mt-8">
            <Button onClick={() => setInquiryOpen(true)} data-testid="product-inquire-btn" className="w-full rounded-none bg-[#171310] hover:bg-[#3a2f1d] text-[#e9d9ac] tracking-[0.15em] uppercase text-xs h-12 transition-colors duration-300">
              Inquire About This Piece
            </Button>
            <div className="grid grid-cols-2 gap-3">
              <a onClick={() => trackContactClick("whatsapp_product")} href={waLink(`Hello Regal Jewels! I'm interested in: ${product.title} (${fmtPrice(product.price)})\nSKU: ${skuFromHandle(product.handle)}\n${productUrl(product.handle)}`)} target="_blank" rel="noopener noreferrer">
                <Button className="w-full rounded-none bg-[#25D366] hover:bg-[#1fb457] text-white tracking-[0.12em] uppercase text-xs h-12 transition-colors duration-300" data-testid="product-whatsapp-btn">
                  <MessageCircle size={15} className="mr-2" /> WhatsApp
                </Button>
              </a>
              <Link to={`/book?product=${encodeURIComponent(product.title)}`}>
                <Button variant="outline" data-testid="product-book-viewing-btn" className="w-full rounded-none border-[#171310] text-[#171310] hover:bg-[#171310] hover:text-[#e9d9ac] tracking-[0.12em] uppercase text-xs h-12 transition-colors duration-300">
                  <CalendarDays size={15} className="mr-2" /> Book Viewing
                </Button>
              </Link>
            </div>
            <a
              onClick={() => trackContactClick("whatsapp_share")}
              href={waShareLink(`Look what I found at Regal Jewels ✨\n${product.title} — ${fmtPrice(product.price)}\n${product.images?.[0] || ""}\n${productUrl(product.handle)}`)}
              target="_blank" rel="noopener noreferrer" className="block"
            >
              <Button variant="outline" className="w-full rounded-none border-[#c9a24b] text-[#8a6a1f] hover:bg-[#c9a24b]/10 tracking-[0.12em] uppercase text-xs h-11 transition-colors duration-300" data-testid="product-share-whatsapp-btn">
                <Share2 size={14} className="mr-2" /> Send to a Friend on WhatsApp
              </Button>
            </a>
            <WishlistButton handle={product.handle} withLabel />
          </div>

          {/* Assurance */}
          <div className="grid grid-cols-3 gap-3 mt-8 border-t border-[#e8e0d0] pt-6 text-center">
            {[
              { icon: ShieldCheck, t: "Purity Guaranteed" },
              { icon: Truck, t: "Insured USA Delivery" },
              { icon: Store, t: "Try In-Store, Chicago" },
            ].map(({ icon: Icon, t }) => (
              <div key={t} className="flex flex-col items-center gap-2">
                <Icon size={20} strokeWidth={1.5} className="text-[#a8842c]" />
                <span className="text-[11px] tracking-wide text-[#7a7160]">{t}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Related */}
      {related.length > 0 && (
        <section className="pt-24">
          <p className="section-eyebrow">You May Also Like</p>
          <h2 className="section-title mb-8">Related Pieces</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
            {related.map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
        </section>
      )}

      {/* Recently viewed */}
      <RecentlyViewed excludeHandle={product.handle} />

      <InquiryDialog product={product} open={inquiryOpen} onOpenChange={setInquiryOpen} />
      {editOpen && (
        <ProductEditModal product={product} open={editOpen} onClose={() => setEditOpen(false)} onSaved={() => setRev((r) => r + 1)} />
      )}
      {viewerOpen && (
        <PhotoViewer
          images={product.images}
          index={imgIdx}
          onIndexChange={setImgIdx}
          onClose={() => setViewerOpen(false)}
          alt={product.title}
        />
      )}
    </div>
  );
}
